import { TypographyVariant } from './applyFluidTypography';

const absoluteMinFontSizeRem = 0.75; // 12px

export const calculateViCoefficient = ({
	maximum,
	minimum,
	minViewportWidth = 320,
	maxViewportWidth = 1000,
}: {
	maximum: number;
	minimum: number;
	minViewportWidth?: number;
	maxViewportWidth?: number;
}): number => {
	// Convert pixel widths to rem units (1rem = 16px)
	const minWidthRem = minViewportWidth / 16;
	const maxWidthRem = maxViewportWidth / 16;
	const viewportRangeRem = maxWidthRem - minWidthRem;

	// Calculate the coefficient for vi units
	// vi units represent 1% of viewport width
	// We want: value = min + (max - min) * (currentViewportWidth - minWidth) / range
	// In vi terms: coefficient = (max - min) / (maxViewportRem / 100)
	const valueDifference = maximum - minimum;
	const viCoefficient = (valueDifference / viewportRangeRem) * 100;

	return Number.parseFloat(viCoefficient.toFixed(3));
};

export const calculateFluidSize = ({
	maximum,
	minimum,
	minViewportWidth = 320,
	maxViewportWidth = 1000,
}: {
	maximum: number;
	minimum?: number;
	minViewportWidth?: number;
	maxViewportWidth?: number;
}) => {
	const mincalc: number = Math.max(minimum ?? maximum * 0.75, absoluteMinFontSizeRem);
	const viCoeff = calculateViCoefficient({
		maximum,
		minimum: mincalc,
		minViewportWidth,
		maxViewportWidth,
	});

	return `clamp(${mincalc}rem, ${viCoeff}vi, ${maximum}rem)`;
};

const VARIANTS: TypographyVariant[] = [
	{
		name: 'heroXL',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 900,
			letterSpacing: '-1px',
			fontSize: calculateFluidSize({ maximum: 7 }),
			lineHeight: calculateFluidSize({ maximum: 6.75 }),
		},
	},
	{
		name: 'heroLarge',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 900,
			letterSpacing: 0,
			fontSize: calculateFluidSize({ maximum: 4 }),
			lineHeight: calculateFluidSize({ maximum: 5 }),
		},
	},
	{
		name: 'heroMedium',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 900,
			letterSpacing: 0,
			fontSize: calculateFluidSize({ maximum: 3.5 }),
			lineHeight: calculateFluidSize({ maximum: 3.75 }),
		},
	},
	{
		name: 'header1',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 900,
			letterSpacing: 0,
			fontSize: calculateFluidSize({ maximum: 3.5 }),
			lineHeight: calculateFluidSize({ maximum: 3.75 }),
		},
	},
	{
		name: 'header2',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 900,
			letterSpacing: 0,
			fontSize: calculateFluidSize({ maximum: 2.25 }),
			lineHeight: calculateFluidSize({ maximum: 2.5 }),
		},
	},
	{
		name: 'header3',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 700,
			letterSpacing: 0,
			fontSize: calculateFluidSize({ maximum: 2 }),
			lineHeight: calculateFluidSize({ maximum: 2.25 }),
		},
	},
	{
		name: 'header4',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 700,
			letterSpacing: 0,
			fontSize: calculateFluidSize({ maximum: 1.625 }),
			lineHeight: calculateFluidSize({ maximum: 1.75 }),
		},
	},
	{
		name: 'bodyXLBook',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 400,
			letterSpacing: 0,
			fontSize: calculateFluidSize({ maximum: 1.375 }),
			lineHeight: calculateFluidSize({ maximum: 1.75 }),
		},
	},
	{
		name: 'bodyLargeBook',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 400,
			letterSpacing: 0,
			fontSize: calculateFluidSize({ maximum: 1.125 }),
			lineHeight: calculateFluidSize({ maximum: 1.75 }),
		},
	},
	{
		name: 'bodyLargeSemiBold',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 700,
			fontSize: calculateFluidSize({ maximum: 1.125 }),
			lineHeight: calculateFluidSize({ maximum: 1.75 }),
		},
	},
	{
		name: 'bodyLargeBold',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 900,
			fontSize: calculateFluidSize({ maximum: 1.125 }),
			lineHeight: calculateFluidSize({ maximum: 1.75 }),
		},
	},
	{
		name: 'bodyMediumBook',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 400,
			fontSize: calculateFluidSize({ maximum: 1 }),
			lineHeight: calculateFluidSize({ maximum: 1.5 }),
		},
	},
	{
		name: 'bodyMediumSemiBold',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 700,
			fontSize: calculateFluidSize({ maximum: 1 }),
			lineHeight: calculateFluidSize({ maximum: 1.5 }),
		},
	},
	{
		name: 'bodyMediumBold',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 900,
			fontSize: calculateFluidSize({ maximum: 1 }),
			lineHeight: calculateFluidSize({ maximum: 1.5 }),
		},
	},
	{
		name: 'bodySmallBook',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 400,
			fontSize: calculateFluidSize({ maximum: 0.875 }),
			lineHeight: calculateFluidSize({ maximum: 1.25 }),
		},
	},
	{
		name: 'bodySmallSemiBold',
		style: {
			fontFamily: 'Lato',
			fontStyle: 'normal',
			fontWeight: 700,
			fontSize: calculateFluidSize({ maximum: 0.875 }),
			lineHeight: calculateFluidSize({ maximum: 1.25 }),
		},
	},
];

export const LOVET_TYPOGRAPHY_VARIANTS: TypographyVariant[] = VARIANTS;
